import {
  ActiveOffer,
  AdminOffer,
  CreateOfferPayload,
  EditOfferPayload,
  ChangeOfferStatusPayload,
  FetchAdminOffersParams,
  AdminOfferListResponse,
  AdminOfferDetailsResponse,
} from '@/types/offer';
import { API_BASE_URL } from '@/config/api';
import type { CartBraceletGift } from '@/services/braceletGiftService';

export type {
  ActiveOffer,
  AdminOffer,
  CreateOfferPayload,
  EditOfferPayload,
  ChangeOfferStatusPayload,
  FetchAdminOffersParams,
  AdminOfferListResponse,
  AdminOfferDetailsResponse,
};

export const AUTH_EXPIRED_EVENT = 'auth:expired';
export const SESSION_EXPIRED_MESSAGE = 'Your session expired. Please sign in again.';

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  status?: number;
}

export interface SendOtpResponse {
  otpID: string;
}

export interface VerifyOtpResponse {
  token: string;
  userId: string;
  phoneNumber: string;
  role?: 'admin' | 'user';
  isProfileUpdated: boolean;
  name: string | null;
  canAccessProducts?: boolean;
}

export interface UserProfile {
  userId: string;
  phoneNumber: string;
  name: string | null;
  email?: string;
  isProfileUpdated: boolean;
}

export interface CartItemPayload {
  productId: string;
  productName: string;
  appPrice: number;
  originalPrice: number;
  imageUrl: string;
  quantity: number;
  category: string;
  description: string;
  variantKey?: string;
  variantCountry?: string;
  variantGrade?: string;
  variantCarat?: number;
}

export interface CartItem {
  isFreeGift?: boolean;
  productId: string;
  productName: string;
  appPrice: number;
  originalPrice: number;
  imageUrl: string;
  quantity: number;
  category: string;
  description: string;
  id?: string;
  price?: number;
  baseUnitPrice?: number;
  effectiveUnitPrice?: number;
  lineBaseTotal?: number;
  lineDiscount?: number;
  lineFinalTotal?: number;
  hasActiveOffer?: boolean;
  activeOffer?: ActiveOffer;
  variantKey?: string;
  variantCountry?: string;
  variantGrade?: string;
  variantCarat?: number;
  name?: string;
  image?: string;
  effectivePrice?: number;
  categorySlug?: string;
  productSlug?: string;
}

export interface AddressRecord {
  addressId?: string;
  id?: string;
  fullName?: string;
  name?: string;
  phoneNumber?: string;
  phone?: string;
  email?: string;
  houseNumber?: string;
  houseFlatNo?: string;
  address?: string;
  city?: string;
  pincode?: string;
  state?: string;
  addressType?: string;
  type?: string;
  isDefault?: boolean;
}

export interface OrderItem {
  userId: string;
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  paymentStatus: string;
  fullName: string;
  phoneNumber: string;
  email: string;
  houseNumber: string;
  address: string;
  city: string;
  pincode: string;
  state: string;
  addressType: string;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  totalPrice: number;
  noOfItems: number;
  currency: string;
  amountInPaisa: string;
  shippingFee?: number;
  promocode?: string;
  promocodeAmount?: number;
  created: number;
  version?: number;
  courier?: string;
  courierTrackingNo?: string;
  trackingLink?: string;
  items?: OrderProductPreview[];
}

export interface OrderProductPreview {
  productName: string;
  imageUrl: string;
  quantity: number;
}

export interface OrderHistoryResponse {
  list: OrderItem[];
  areMoreItemsAvailable: boolean;
  page?: number;
  pageSize?: number;
  totalCount?: number;
  totalPages?: number;
}

export interface OrderSummaryResponse {
  all: number;
  created: number;
  packed: number;
  shipped: number;
  delivered: number;
  canceled: number;
  returned: number;
  pendingPayment: number;
  paid: number;
}

export interface OrderStatusHistoryEntry {
  eventType?: 'ORDER_CREATED' | 'PAYMENT_CONFIRMED' | 'STATUS_CHANGED' | string;
  fromStatus?: string;
  toStatus: string;
  changedAt: number;
  changedByUserId?: string;
  changedByRole?: string;
  reason?: string;
  note?: string;
  courier?: string;
  trackingNumber?: string;
}

export interface OrderProduct {
  productId: string;
  quantity: number;
  appPrice: number;
  originalPrice: number;
  productName: string;
  imageUrl: string;
  description: string;
  category: string;
  userId: string;
  created: number;
  orderId: string;
  orderNumber: string;
}

export interface OrderDetailsResponse {
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  paymentStatus: string;
  totalPrice: number;
  noOfItems: number;
  currency?: string;
  amountInPaisa?: string;
  shippingFee?: number;
  promocode?: string;
  promocodeAmount?: number;
  created: number;
  fullName: string;
  phoneNumber: string;
  email: string;
  houseNumber: string;
  address: string;
  city: string;
  pincode: string;
  state: string;
  addressType: string;
  list: OrderProduct[];
  courier?: string;
  courierTrackingNo?: string;
  trackingLink?: string;
  shiprocket?: ShiprocketShipment;
  shippingTrackingStatus?: string;
  shippingTrackingLocation?: string;
  shippingTrackingUpdatedAt?: number;
  packed?: number;
  shipped?: number;
  delivered?: number;
  canceled?: number;
  returned?: number;
  version: number;
  statusHistory?: OrderStatusHistoryEntry[];
  refundStatus?: 'NOT_REQUIRED' | 'PENDING' | 'PROCESSING' | 'REFUNDED' | 'PARTIALLY_REFUNDED' | 'FAILED' | string;
  subtotal?: number;
  baseSubtotal?: number;
  subtotalAfterAutomaticOffers?: number;
  finalTotal?: number;
  automaticOfferDiscount?: number;
  offerDiscount?: number;
  couponDiscount?: number;
  paidAmount?: number;
  refundedAmount?: number;
  razorpay_order_id?: string;
  razorpay_payment_id?: string;
  razorpayRefundIds?: string[];
  paymentStatusVerifiedAt?: number;
}

export interface UpdateOrderStatusPayload {
  orderId: string;
  status: string;
  expectedVersion: number;
  courier?: string;
  courierTrackingNo?: string;
  trackingLink?: string;
  reason?: string;
  note?: string;
}

export interface DashboardStatsResponse {
  shipped: number;
  shippedvalue: number;
  created: number;
  createdvalue: number;
  packed: number;
  packedvalue: number;
  delivered: number;
  deliveredvalue: number;
  canceled: number;
  canceledvalue: number;
  returned: number;
  returnedvalue: number;
}

export interface DashboardRevenueTrendItem {
  _id: string;
  totalRevenue: number;
}

export interface DashboardRevenueTrendResponse {
  list: DashboardRevenueTrendItem[];
}

export interface CreateProductPayload {
  productName: string;
  originalPrice: number;
  appPrice: number;
  category: string;
  benefits: string[];
  description: string;
  imageUrl?: string;
  urls?: string[];
  videoUrls?: string[];
  isActive: boolean;
  suitableFor?: string;
  howToWear?: string;
  bestDayToWear?: string;
  disclaimer?: string;
  packaging?: string;
  returnPolicy?: string;
  gemstoneVariants?: { country: string; grade: string; carat: number; price: number; originalPrice?: number }[];
}

export interface UpdateProductPayload {
  productId: string;
  productName: string;
  originalPrice: number;
  appPrice: number;
  category: string;
  benefits: string[];
  description: string;
  imageUrl?: string;
  urls?: string[];
  videoUrls?: string[];
  isActive: boolean;
  suitableFor?: string;
  howToWear?: string;
  bestDayToWear?: string;
  disclaimer?: string;
  packaging?: string;
  returnPolicy?: string;
  gemstoneVariants?: { country: string; grade: string; carat: number; price: number; originalPrice?: number }[];
}

export interface ImageUploadResponse {
  url: string;
}

export interface CreateBannerPayload {
  mediaType: 'image' | 'video';
  mediaUrl: string;
  mobileMediaUrl?: string;
  placement?: 'home' | 'category';
  category?: string;
  redirectUrl?: string;
  buttonText?: string;
  isActive: boolean;
  displayOrder?: number;
}

export interface UpdateBannerPayload {
  bannerId: string;
  mediaType?: 'image' | 'video';
  mediaUrl?: string;
  mobileMediaUrl?: string;
  placement?: 'home' | 'category';
  category?: string;
  redirectUrl?: string;
  buttonText?: string;
  isActive?: boolean;
  displayOrder?: number;
}

export interface CreateReviewPayload {
  productId: string;
  rating: number;
  reviewerName?: string;
  userName?: string;
  review?: string;
  text?: string;
  imageUrl?: string;
  videoUrl?: string;
}

export interface AdminCreateReviewPayload {
  productId: string;
  rating: number;
  userName?: string;
  text?: string;
  imageUrl?: string;
  videoUrl?: string;
}

export interface EditReviewPayload {
  reviewId: string;
  rating?: number;
  userName?: string;
  text?: string;
  imageUrl?: string;
  videoUrl?: string;
}

export interface AvailableCoupon {
  couponId: string;
  code: string;
  title: string;
  discountType: 'percent' | 'flat';
  discountValue: number;
  maxDiscount: number;
  minOrderAmount?: number;
  eligibleSubtotal: number;
  discount: number;
  eligible: boolean;
  alreadyUsed: boolean;
  reason?: string;
  startsAt?: number;
  expiresAt?: number;
  sourceType?: 'GEMFINDER' | string;
  ownerUserId?: string;
}

export interface ShiprocketShipment {
  state: string;
  orderId?: number;
  shipmentId?: number;
  courierId?: number;
  courierName?: string;
  quotedRate?: number;
  estimatedDeliveryDays?: string;
  awb?: string;
  currentStatus?: string;
  pickupRequested?: boolean;
  pickupRequestState?: string;
  pickupScheduledDate?: string;
  cancelled?: boolean;
  cancelRequestState?: string;
  labelUrl?: string;
  latestEvent?: { status: string; location?: string; at: number };
}

export interface ShippingQuote {
  courierId: number;
  courierName: string;
  rate: number;
  estimatedDeliveryDays: string;
}

export interface PackageSize {
  weight: number;
  length: number;
  breadth: number;
  height: number;
}

export interface OrderExportItem extends Omit<OrderItem, 'items'> {
  baseSubtotal?: number;
  automaticOfferDiscount?: number;
  subtotalAfterAutomaticOffers?: number;
  couponDiscount?: number;
  finalTotal?: number;
  paidAmount?: number;
  refundedAmount?: number;
  refundStatus?: string;
  items: Array<Partial<OrderProduct> & {
    effectiveUnitPrice?: number;
    baseUnitPrice?: number;
    lineFinalTotal?: number;
    variantCountry?: string;
    variantGrade?: string;
    variantCarat?: number;
  }>;
}

export interface OrderExportPage {
  list: OrderExportItem[];
  areMoreItemsAvailable: boolean;
}

export interface AvailableCouponsResponse {
  list: AvailableCoupon[];
}

export interface ApplyCouponResponse {
  couponId?: string;
  code: string;
  title?: string;
  discountType: 'flat' | 'percent';
  discountValue: number;
  maxDiscount?: number;
  eligibleSubtotal?: number;
  discount: number;
  eligible?: boolean;
  message?: string;
  sourceType?: 'GEMFINDER' | string;
  ownerUserId?: string;
}

export interface CreateCouponPayload {
  title: string;
  code: string;
  visibility: 'public' | 'private';
  appliesTo: 'shop' | 'gemfinder';
  scope: 'all' | 'category' | 'product';
  category?: string;
  productIds?: string[];
  discountType: 'flat' | 'percent';
  discountValue: number;
  minOrderAmount?: number;
  maxDiscount?: number;
  maxUses?: number;
  maxUsesPerUser?: number;
  startsAt?: number;
  expiresAt?: number;
  influencerName?: string;
  campaignName?: string;
  isActive: boolean;
}

export interface EditCouponPayload {
  couponId: string;
  title?: string;
  code?: string;
  visibility?: 'public' | 'private';
  appliesTo?: 'shop' | 'gemfinder';
  scope?: 'all' | 'category' | 'product';
  category?: string;
  productIds?: string[];
  discountType?: 'flat' | 'percent';
  discountValue?: number;
  minOrderAmount?: number;
  maxDiscount?: number;
  maxUses?: number;
  maxUsesPerUser?: number;
  startsAt?: number;
  expiresAt?: number;
  influencerName?: string;
  campaignName?: string;
  isActive?: boolean;
}

export interface SecureCreateOrderPayload {
  addressId: string;
  isOnline: boolean;
  promocode?: string;
  expectedAmountInPaisa: number;
  expectedGiftProductId?: string;
}

export interface CheckoutQuoteItem {
  isFreeGift?: boolean;
  productId: string;
  variantKey?: string;
  productName: string;
  imageUrl: string;
  quantity: number;
  effectiveUnitPrice: number;
  lineFinalTotal: number;
}

export interface CheckoutQuoteResponse {
  braceletGift?: CartBraceletGift;
  list: CheckoutQuoteItem[];
  baseSubtotal: number;
  automaticOfferDiscount: number;
  subtotalAfterAutomaticOffers: number;
  couponDiscount: number;
  shippingFee: number;
  finalTotal: number;
  amountInPaisa: number;
  currency: string;
  promocode: string;
}

export interface SecureCreateOrderResponse {
  order_id: string; // Razorpay order ID
  currency: string;
  amount: number; // in paisa
  orderId: string; // internal backend order ID
  orderNumber?: string;
  subtotal?: number;
  baseSubtotal?: number;
  automaticOfferDiscount?: number;
  subtotalAfterAutomaticOffers?: number;
  couponDiscount?: number;
  shippingFee?: number;
  promocodeAmount?: number;
  totalPrice?: number;
  finalTotal?: number;
  success?: boolean;
  keyId?: string;
}

export interface PaymentVerificationPayload {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  orderId: string;
}

export interface PaymentVerificationResponse {
  success: boolean;
  orderNumber?: string;
  message?: string;
}

export interface CreateBlogPayload {
  title: string;
  language: 'en' | 'te' | 'hi';
  slug?: string;
  summary?: string;
  content: string;
  coverImageUrl?: string;
  author?: string;
  isActive: boolean;
}

export interface UpdateBlogPayload {
  blogId: string;
  language?: 'en' | 'te' | 'hi';
  title?: string;
  slug?: string;
  summary?: string;
  content?: string;
  coverImageUrl?: string;
  author?: string;
  isActive?: boolean;
}

export interface BlogItem {
  blogId: string;
  language?: 'en' | 'te' | 'hi';
  title: string;
  slug?: string;
  summary?: string;
  content: string;
  coverImageUrl?: string;
  author?: string;
  isActive?: boolean;
  created?: number;
  updated?: number;
}

export interface BlogListResponse {
  list: BlogItem[];
  areMoreItemsAvailable: boolean;
}

class ApiService {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  // Signing in anywhere revokes older sessions server-side, so a 401 on a
  // request we authenticated means this tab is holding a dead token.
  private handleUnauthorized(status: number): boolean {
    if (status !== 401 || !this.token) return false;
    this.token = null;
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    return true;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...options.headers,
      };

      if (this.token) {
        headers['token'] = this.token;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const rawError = await response.text();
        let error = rawError;
        try {
          const parsed: unknown = JSON.parse(rawError);
          if (parsed && typeof parsed === 'object') {
            const envelope = parsed as Record<string, unknown>;
            const readable = envelope.message ?? envelope.error ?? envelope.detail;
            if (typeof readable === 'string' && readable.trim()) error = readable;
          }
        } catch {
          // Non-JSON error responses are already readable as plain text.
        }
        if (this.handleUnauthorized(response.status)) {
          return { success: false, error: SESSION_EXPIRED_MESSAGE };
        }
        return {
          success: false,
          error: error || `HTTP error! status: ${response.status}`,
          status: response.status,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An error occurred',
      };
    }
  }

  async sendOtp(phoneNumber: string): Promise<ApiResponse<SendOtpResponse>> {
    return this.request<SendOtpResponse>('/api/auth/send-otp', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber }),
    });
  }

  async verifyOtp(
    phoneNumber: string,
    otp: string,
    otpID: string
  ): Promise<ApiResponse<VerifyOtpResponse>> {
    return this.request<VerifyOtpResponse>('/api/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber, otp, otpID }),
    });
  }

  async getProfile(): Promise<ApiResponse<UserProfile>> {
    return this.request<UserProfile>('/api/auth/profile', {
      method: 'GET',
    });
  }

  async updateProfile(payload: { name?: string; email?: string }): Promise<ApiResponse<void>> {
    return this.request<void>('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async logout(): Promise<ApiResponse<void>> {
    const result = await this.request<void>(`/api/auth/logout?token=${this.token}`, {
      method: 'DELETE',
    });
    if (result.success) {
      this.token = null;
    }
    return result;
  }

  async addToCart(item: CartItemPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/cart/add', {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async getCart(): Promise<ApiResponse<CartItem[] | { list: CartItem[]; braceletGift?: CartBraceletGift }>> {
    return this.request<CartItem[] | { list: CartItem[]; braceletGift?: CartBraceletGift }>('/api/cart', {
      method: 'GET',
    });
  }

  async updateCartItem(productId: string, quantity: number, variantKey?: string): Promise<ApiResponse<void>> {
    const body: { productId: string; quantity: number; variantKey?: string } = { productId, quantity };
    if (variantKey) body.variantKey = variantKey;
    return this.request<void>('/api/cart/update', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async removeFromCart(productId: string, variantKey?: string): Promise<ApiResponse<void>> {
    // backend parses "productId|variantKey" for variants
    const key = variantKey ? `${productId}|${variantKey}` : productId;
    return this.request<void>(`/api/cart/remove?productId=${encodeURIComponent(key)}`, {
      method: 'DELETE',
    });
  }

  async clearCart(): Promise<ApiResponse<void>> {
    return this.request<void>('/api/cart/clear', {
      method: 'DELETE',
    });
  }

  async getAddresses(): Promise<ApiResponse<AddressRecord[] | { list: AddressRecord[] }>> {
    return this.request<AddressRecord[] | { list: AddressRecord[] }>('/api/address', {
      method: 'GET',
    });
  }

  async addAddress(address: {
    fullName: string;
    phoneNumber: string;
    email: string;
    houseNumber: string;
    address: string;
    city: string;
    pincode: string;
    state: string;
    addressType: string;
  }): Promise<ApiResponse<void>> {
    return this.request<void>('/api/address/add', {
      method: 'POST',
      body: JSON.stringify(address),
    });
  }

  async updateAddress(addressId: string, address: {
    fullName: string;
    phoneNumber: string;
    email: string;
    houseNumber: string;
    address: string;
    city: string;
    pincode: string;
    state: string;
    addressType: string;
  }): Promise<ApiResponse<void>> {
    return this.request<void>('/api/address/update', {
      method: 'PUT',
      body: JSON.stringify({ addressId, ...address }),
    });
  }

  async removeAddress(addressId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/address/remove?addressId=${addressId}`, {
      method: 'DELETE',
    });
  }

  async getOrderHistory(count: number = 20, offset: number = 0): Promise<ApiResponse<OrderHistoryResponse>> {
    return this.request<OrderHistoryResponse>(`/api/order/history?count=${count}&offset=${offset}`, {
      method: 'GET',
    });
  }

  async getOrderDetails(orderId: string): Promise<ApiResponse<OrderDetailsResponse>> {
    return this.request<OrderDetailsResponse>(`/api/order/details?orderId=${orderId}`, {
      method: 'GET',
    });
  }

  // Admin endpoints
  async getAdminOrders(
    count: number = 20,
    offset: number = 0,
    status?: string,
    paymentStatus?: string,
    search?: string,
    dateFrom?: number,
    dateTo?: number
  ): Promise<ApiResponse<OrderHistoryResponse>> {
    const params = new URLSearchParams({
      count: count.toString(),
      offset: offset.toString(),
      status: status || '',
      paymentStatus: paymentStatus || '',
      search: search || '',
      dateFrom: dateFrom?.toString() || '0',
      dateTo: dateTo?.toString() || '0',
    });
    
    return this.request<OrderHistoryResponse>(`/api/admin/orders?${params.toString()}`, { cache: 'no-store' });
  }

  async getAdminOrderExportPage(
    count: number,
    offset: number,
    status: string,
    paymentStatus: string,
    search: string,
    dateFrom: number,
    dateTo: number
  ): Promise<ApiResponse<OrderExportPage>> {
    const params = new URLSearchParams({
      count: String(count), offset: String(offset), status, paymentStatus, search,
      dateFrom: String(dateFrom), dateTo: String(dateTo),
    });
    return this.request<OrderExportPage>(`/api/admin/orders/export?${params.toString()}`, { cache: 'no-store' });
  }

  async updateOrderStatus(
    payload: UpdateOrderStatusPayload
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/admin/orders/update`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

  async getAdminOrdersSummary(
    dateFrom?: number,
    dateTo?: number
  ): Promise<ApiResponse<OrderSummaryResponse>> {
    const params = new URLSearchParams();
    if (dateFrom) params.set('dateFrom', String(dateFrom));
    if (dateTo) params.set('dateTo', String(dateTo));
    const query = params.toString();
    return this.request<OrderSummaryResponse>(`/api/admin/orders/summary${query ? `?${query}` : ''}`, { cache: 'no-store' });
  }

  async getAdminOrderDetails(orderId: string): Promise<ApiResponse<OrderDetailsResponse>> {
    return this.request<OrderDetailsResponse>(`/api/admin/order/details?orderId=${orderId}`);
  }

  async getOrderLogistics(orderId: string): Promise<ApiResponse<{ configured: boolean; shipment?: ShiprocketShipment }>> {
    return this.request(`/api/admin/orders/${encodeURIComponent(orderId)}/logistics`);
  }

  async getShippingQuotes(orderId: string, size: PackageSize): Promise<ApiResponse<{ quotes: ShippingQuote[] }>> {
    return this.request(`/api/admin/orders/${encodeURIComponent(orderId)}/logistics/quotes`, {
      method: 'POST', body: JSON.stringify(size),
    });
  }

  async createShipment(orderId: string, size: PackageSize, courierId: number): Promise<ApiResponse<ShiprocketShipment>> {
    return this.request(`/api/admin/orders/${encodeURIComponent(orderId)}/logistics/create`, {
      method: 'POST', body: JSON.stringify({ ...size, courierId }),
    });
  }

  async bookShipment(orderId: string, size?: PackageSize, courierId?: number): Promise<ApiResponse<ShiprocketShipment>> {
    return this.request(`/api/admin/orders/${encodeURIComponent(orderId)}/logistics/book`, {
      method: 'POST', body: JSON.stringify({ ...size, ...(courierId ? { courierId } : {}) }),
    });
  }

  async assignShipmentAwb(orderId: string): Promise<ApiResponse<ShiprocketShipment>> {
    return this.request(`/api/admin/orders/${encodeURIComponent(orderId)}/logistics/assign`, { method: 'POST' });
  }

  async requestShipmentPickup(orderId: string): Promise<ApiResponse<ShiprocketShipment>> {
    return this.request(`/api/admin/orders/${encodeURIComponent(orderId)}/logistics/pickup`, { method: 'POST' });
  }

  async generateShipmentLabel(orderId: string): Promise<ApiResponse<ShiprocketShipment>> {
    return this.request(`/api/admin/orders/${encodeURIComponent(orderId)}/logistics/label`, { method: 'POST' });
  }

  async cancelShipment(orderId: string): Promise<ApiResponse<ShiprocketShipment>> {
    return this.request(`/api/admin/orders/${encodeURIComponent(orderId)}/logistics/cancel`, { method: 'POST' });
  }

  async getDashboardStats(dateFrom: number = 0, dateTo: number = 0): Promise<ApiResponse<DashboardStatsResponse>> {
    return this.request<DashboardStatsResponse>(`/api/admin/dashboard/v1?dateFrom=${dateFrom}&dateTo=${dateTo}`);
  }

  async getDashboardRevenueTrend(dateFrom: number = 0, dateTo: number = 0): Promise<ApiResponse<DashboardRevenueTrendResponse>> {
    return this.request<DashboardRevenueTrendResponse>(`/api/admin/dashboard/v2?dateFrom=${dateFrom}&dateTo=${dateTo}`);
  }

  async createProduct(product: CreateProductPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/product/create', {
      method: 'POST',
      body: JSON.stringify(product),
    });
  }

  async updateProduct(product: UpdateProductPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/product/edit', {
      method: 'PUT',
      body: JSON.stringify(product),
    });
  }

  async deleteProduct(productId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/product/delete?productId=${encodeURIComponent(productId)}`, {
      method: 'DELETE',
    });
  }

  async createBlog(blog: CreateBlogPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/blog/create', {
      method: 'POST',
      body: JSON.stringify(blog),
    });
  }

  async updateBlog(blog: UpdateBlogPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/blog/edit', {
      method: 'PUT',
      body: JSON.stringify(blog),
    });
  }

  async deleteBlog(blogId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/blog/delete?blogId=${encodeURIComponent(blogId)}`, {
      method: 'DELETE',
    });
  }

  async fetchAdminBlogs(text: string = ''): Promise<ApiResponse<BlogListResponse>> {
    const params = new URLSearchParams();
    if (text) params.append('text', text);
    return this.request<BlogListResponse>(`/api/blog/fetch?${params.toString()}`);
  }

  async fetchAdminBlogDetails(blogId: string): Promise<ApiResponse<BlogItem>> {
    return this.request<BlogItem>(`/api/blog/details?blogId=${encodeURIComponent(blogId)}`);
  }

  async uploadImage(file: File, type?: number): Promise<ApiResponse<ImageUploadResponse>> {
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (typeof type === 'number') {
        formData.append('type', String(type));
      }

      const headers: HeadersInit = {};
      if (this.token) {
        headers['token'] = this.token;
      }

      const response = await fetch(`${API_BASE_URL}/upload/single-image`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const error = await response.text();
        if (this.handleUnauthorized(response.status)) {
          return { success: false, error: SESSION_EXPIRED_MESSAGE };
        }
        return {
          success: false,
          error: error || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An error occurred',
      };
    }
  }

  async createBanner(payload: CreateBannerPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/banner/create', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateBanner(payload: UpdateBannerPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/banner/edit', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteBanner(bannerId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/banner/delete?bannerId=${encodeURIComponent(bannerId)}`, {
      method: 'DELETE',
    });
  }

  // Reviews
  async createReview(payload: CreateReviewPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/review/create', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteReview(reviewId: string): Promise<ApiResponse<void>> {
    const primaryRes = await this.request<void>(`/api/review/${encodeURIComponent(reviewId)}`, {
      method: 'DELETE',
    });
    if (!primaryRes.success && primaryRes.error?.includes('404')) {
      return this.request<void>(`/api/review/delete?reviewId=${encodeURIComponent(reviewId)}`, {
        method: 'DELETE',
      });
    }
    return primaryRes;
  }

  async adminCreateReview(payload: AdminCreateReviewPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/admin/review/create', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async adminEditReview(payload: EditReviewPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/admin/review/edit', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async adminDeleteReview(reviewId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/admin/review/delete?reviewId=${encodeURIComponent(reviewId)}`, {
      method: 'DELETE',
    });
  }

  // Coupons
  async getAvailableCoupons(): Promise<ApiResponse<AvailableCouponsResponse>> {
    return this.request<AvailableCouponsResponse>('/api/coupon/available', {
      method: 'GET',
    });
  }

  async applyCoupon(code: string): Promise<ApiResponse<ApplyCouponResponse>> {
    return this.request<ApplyCouponResponse>('/api/coupon/apply', {
      method: 'POST',
      body: JSON.stringify({ code: code.trim().toUpperCase() }),
    });
  }

  async createCoupon(payload: CreateCouponPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/admin/coupon/create', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async editCoupon(payload: EditCouponPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/admin/coupon/edit', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteCoupon(couponId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/admin/coupon/delete?couponId=${encodeURIComponent(couponId)}`, {
      method: 'DELETE',
    });
  }

  // Timed Automatic Offers (Admin)
  async createOffer(payload: CreateOfferPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/admin/offer/create', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async editOffer(payload: EditOfferPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/admin/offer/edit', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async getAdminOffers(params: FetchAdminOffersParams = {}): Promise<ApiResponse<AdminOfferListResponse | AdminOffer[]>> {
    const query = new URLSearchParams();
    if (params.search) query.append('search', params.search);
    if (params.status) query.append('status', params.status);
    if (params.scope) query.append('scope', params.scope);
    if (params.includeArchived !== undefined) query.append('includeArchived', String(params.includeArchived));

    return this.request<AdminOfferListResponse | AdminOffer[]>(`/api/admin/offer/fetch?${query.toString()}`);
  }

  async getAdminOfferDetails(offerId: string): Promise<ApiResponse<AdminOfferDetailsResponse | AdminOffer>> {
    return this.request<AdminOfferDetailsResponse | AdminOffer>(`/api/admin/offer/details?offerId=${encodeURIComponent(offerId)}`);
  }

  async publishOffer(payload: ChangeOfferStatusPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/admin/offer/publish', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async pauseOffer(payload: ChangeOfferStatusPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/admin/offer/pause', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async resumeOffer(payload: ChangeOfferStatusPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/admin/offer/resume', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deactivateOffer(payload: ChangeOfferStatusPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/admin/offer/deactivate', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteOffer(offerId: string, version: number): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/admin/offer/delete?offerId=${encodeURIComponent(offerId)}&version=${version}`, {
      method: 'DELETE',
      body: JSON.stringify({ offerId, version }),
    });
  }

  async archiveOffer(payload: ChangeOfferStatusPayload): Promise<ApiResponse<void>> {
    return this.request<void>('/api/admin/offer/archive', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // Secure Checkout & Order
  async getCheckoutQuote(promocode?: string): Promise<ApiResponse<CheckoutQuoteResponse>> {
    const code = promocode?.trim() ?? '';
    return this.request<CheckoutQuoteResponse>(`/api/checkout/quote?promocode=${encodeURIComponent(code)}`, {
      method: 'GET',
      cache: 'no-store',
    });
  }

  async createSecureOrder(payload: SecureCreateOrderPayload): Promise<ApiResponse<SecureCreateOrderResponse>> {
    return this.request<SecureCreateOrderResponse>('/api/checkout/create/order', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async verifyPayment(payload: PaymentVerificationPayload): Promise<ApiResponse<PaymentVerificationResponse>> {
    return this.request<PaymentVerificationResponse>('/api/checkout/verify/order', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

export const apiService = new ApiService();
